import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Send, Loader2 } from "lucide-react";

const MAX = 320;

// Select-all + "Send selected via Gingr" toolbar for the renewal drafts list.
export const BulkSendBar = ({ api, picks, allOn, onToggleAll, eligible, onDone }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const bad = picks.filter((p) => !p.text.trim() || [...p.text].length > MAX);
  const send = async () => {
    setBusy(true);
    try {
      const r = (await axios.post(`${api}/send-bulk`, { items: picks.map((p) => ({ key: p.key, text: p.text.trim() })) })).data;
      toast.success(`Sending ${r.queued} text${r.queued === 1 ? "" : "s"} via Gingr — one at a time, ~30s each`);
      if (r.skipped?.length) toast.warning(`${r.skipped.length} skipped (already sending or invalid)`);
      await onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk send failed"); } finally { setBusy(false); }
  };
  return (
    <div className="sticky top-2 z-10 flex flex-wrap items-center gap-3 bg-white/90 backdrop-blur-md border border-gray-200 rounded-xl px-3 py-2 shadow-sm" data-testid="pkg-bulk-bar">
      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 cursor-pointer">
        <Checkbox checked={allOn} disabled={!eligible} onCheckedChange={onToggleAll} data-testid="pkg-select-all" />
        Select all not yet texted
      </label>
      <span className="text-xs text-gray-500" data-testid="pkg-selected-count">{picks.length} selected</span>
      <Button size="sm" className="ml-auto bg-gradient-to-r from-emerald-500 to-emerald-600" disabled={!picks.length || busy || bad.length > 0}
        onClick={() => setOpen(true)} data-testid="pkg-bulk-send-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
        Send {picks.length || ""} via Gingr
      </Button>
      {bad.length > 0 && <span className="w-full text-xs text-rose-600">{bad.length} selected draft(s) are empty or over {MAX} characters.</span>}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="pkg-bulk-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Text {picks.length} customer{picks.length === 1 ? "" : "s"} via Gingr?</AlertDialogTitle>
            <AlertDialogDescription>Each customer gets their own draft exactly as shown below the table: {picks.map((p) => p.name).join(", ")}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pkg-bulk-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={send} data-testid="pkg-bulk-confirm-send">Send all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
